import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  CircularProgress, Alert, IconButton, Switch, FormControlLabel, Tooltip
} from '@mui/material';
import { Refresh, Add, Edit, Delete, Percent } from '@mui/icons-material';
import axios from 'axios';

const API = 'https://api.bharatdarshan.app/api';
const empty = { categoryId: '', categoryName: '', ruleType: 'PERCENTAGE', value: 10, tdsPercent: 0.5, gstPercent: 18, isActive: true, note: '' };

const CommissionRules = () => {
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(null);
  const [snack, setSnack] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        axios.get(`${API}/marketplace/admin/commission-rules`),
        axios.get(`${API}/marketplace/categories`).catch(() => ({ data: { data: [] } })),
      ]);
      setRules(r.data?.data || []);
      setCategories(c.data?.data || c.data?.categories || []);
    } catch (e) { setSnack({ type: 'error', msg: e.message }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form };
      if (!body.categoryId) delete body.categoryId;
      if (form._id) {
        await axios.put(`${API}/marketplace/admin/commission-rules/${form._id}`, body);
        setSnack({ type: 'success', msg: 'Rule updated' });
      } else {
        await axios.post(`${API}/marketplace/admin/commission-rules`, body);
        setSnack({ type: 'success', msg: 'Rule created' });
      }
      setForm(null); load();
    } catch (e) { setSnack({ type: 'error', msg: e.response?.data?.message || e.message }); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      await axios.delete(`${API}/marketplace/admin/commission-rules/${id}`);
      setSnack({ type: 'success', msg: 'Deleted' });
      load();
    } catch (e) { setSnack({ type: 'error', msg: e.message }); }
  };

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  const defaultRule = rules.find(r => !r.categoryId && r.isActive) || null;
  const customRules = rules.filter(r => r.categoryId);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>Commission Rules</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} sx={{ bgcolor: '#4CAF50' }} onClick={() => setForm({ ...empty })}>New Rule</Button>
        </Box>
      </Box>

      {snack && <Alert severity={snack.type} sx={{ mb: 2 }} onClose={() => setSnack(null)}>{snack.msg}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#2196F3', color: 'white' }}><CardContent>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{defaultRule ? `${defaultRule.value}%` : '—'}</Typography>
            <Typography>Default Commission</Typography>
            <Typography variant="caption">No category match</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#FF9800', color: 'white' }}><CardContent>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{defaultRule ? `${defaultRule.tdsPercent}%` : '0.5%'}</Typography>
            <Typography>TDS Rate</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#9C27B0', color: 'white' }}><CardContent>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{defaultRule ? `${defaultRule.gstPercent}%` : '18%'}</Typography>
            <Typography>GST on Commission</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#4CAF50', color: 'white' }}><CardContent>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{customRules.length}</Typography>
            <Typography>Category-Specific Rules</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Paper>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Percent color="primary" />
          <Typography variant="h6">All Rules ({rules.length})</Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><b>Category</b></TableCell>
                <TableCell><b>Type</b></TableCell>
                <TableCell align="right"><b>Value</b></TableCell>
                <TableCell align="right"><b>TDS %</b></TableCell>
                <TableCell align="right"><b>GST %</b></TableCell>
                <TableCell><b>Effective From</b></TableCell>
                <TableCell><b>Active</b></TableCell>
                <TableCell><b>Note</b></TableCell>
                <TableCell><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map(r => (
                <TableRow key={r._id} hover>
                  <TableCell>
                    {r.categoryId ? (r.categoryId?.categoryName || r.categoryId?.name || r.categoryName || r.categoryId?.title) : <Chip size="small" label="DEFAULT" color="primary" />}
                  </TableCell>
                  <TableCell>{r.ruleType}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{r.ruleType === 'PERCENTAGE' ? `${r.value}%` : `₹${r.value}`}</TableCell>
                  <TableCell align="right">{r.tdsPercent}%</TableCell>
                  <TableCell align="right">{r.gstPercent}%</TableCell>
                  <TableCell>{new Date(r.effectiveFrom).toLocaleDateString('en-IN')}</TableCell>
                  <TableCell><Chip size="small" label={r.isActive ? 'Active' : 'Inactive'} color={r.isActive ? 'success' : 'default'} /></TableCell>
                  <TableCell><Typography variant="caption">{r.note || '—'}</Typography></TableCell>
                  <TableCell>
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => setForm({ ...r, categoryId: r.categoryId?._id || r.categoryId || '' })}><Edit fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => remove(r._id)}><Delete fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {rules.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center"><Typography color="textSecondary" sx={{ py: 3 }}>No rules. Create one with "New Rule".</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{form?._id ? 'Edit Rule' : 'New Commission Rule'}</DialogTitle>
        <DialogContent dividers>
          {form && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField select label="Category" value={form.categoryId || ''} onChange={(e) => setForm({ ...form, categoryId: e.target.value })} helperText="Empty = default rule (all categories)">
                <MenuItem value="">— Default (All) —</MenuItem>
                {categories.map(c => (<MenuItem key={c._id} value={c._id}>{c.categoryName || c.name || c.title}</MenuItem>))}
              </TextField>
              <TextField select label="Rule Type" value={form.ruleType} onChange={(e) => setForm({ ...form, ruleType: e.target.value })}>
                <MenuItem value="PERCENTAGE">Percentage (%)</MenuItem>
                <MenuItem value="FIXED">Fixed Amount (₹)</MenuItem>
              </TextField>
              <TextField type="number" label={form.ruleType === 'PERCENTAGE' ? 'Commission %' : 'Fixed ₹'} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} inputProps={{ min: 0 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}><TextField type="number" label="TDS %" value={form.tdsPercent} onChange={(e) => setForm({ ...form, tdsPercent: e.target.value })} fullWidth /></Grid>
                <Grid item xs={6}><TextField type="number" label="GST %" value={form.gstPercent} onChange={(e) => setForm({ ...form, gstPercent: e.target.value })} fullWidth /></Grid>
              </Grid>
              <TextField label="Note" value={form.note || ''} onChange={(e) => setForm({ ...form, note: e.target.value })} multiline rows={2} />
              <FormControlLabel control={<Switch checked={!!form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />} label="Active" />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForm(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#4CAF50' }} onClick={save} disabled={busy || form?.value === undefined || form?.value === ''}>{busy ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommissionRules;
